import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { formatMoney } from '../../util/currency';
import { Money } from 'sharetribe-flex-sdk/src/types';
import { withViewport } from '../../util/contextHelpers';
import { propTypes } from '../../util/types';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { getDefaultTimeZoneOnBrowser } from '../../util/dates';
import BookingBreakdownNew from '../../components/BookingBreakdown/BookingBreakdownNew';
import config from '../../config';
import { getUserTxRole } from '../../util/transaction';
import { ensureBooking, ensureUser, restoreTransaction } from '../../util/data';
import {
  fetchFuelChildTransaction,
  fetchUpdateBookingTransaction,
} from '../TransactionPage/TransactionPage.duck';

import {
  BookingInfoSection,
  IconSpinner,
  Logo,
  NamedLink,
  Page,
  PartnerInfo,
} from '../../components';
import { $bookingTx, loadData } from './TripDetailsPage.duck';

import css from './TripDetailsPage.css';
import FuelInclusionBreakdown from '../../components/BookingBreakdown/FuelInclusionBreakdown';
import { $fuelPriceByListing } from '../../ducks/FuelPrice.duck';
import HostFuelPackageBlock from '../../components/BookingBreakdown/HostFuelPackageBlock';
import UpdateBookingTxs from '../../components/TripPanel/UpdateBookingTxs';
import { $lastUpdateBookingTxByParentTxId, $updateBookingTxsByParentTxId } from '../EditTripPage/EditTripPage.duck';



const PROVIDER = 'provider';
const CUSTOMER = 'customer';
const MAX_MOBILE_SCREEN_WIDTH = 768;
const MAX_TABLET_SCREEN_WIDTH = 1024;

// TransactionPage handles data loading for Sale and Order views to transaction pages in Inbox.
export const TripDetailsPageComponent = props => {
  const { action = '' } = useParams();
  const {
    lastUpdateBookingTx,
    updateBookingTxs,
    fuelPriceByListing,
    scrollingDisabled,
    transaction: parentTransaction,
    intl,
    transactionRole,
    viewport,
    currentUser,
    history,
    fetchFuelChildTransaction,
    fuelTransaction,
    accessToken,
    hostTransactionData,
    fetchHostTransactionInProgress,
    fetchTransactionInProgress
  } = props;
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isUsingMastercard, setIsUsingMastercard] = useState(false);

  const dispatch = useDispatch();
  
  // Use URL action for provider detection (like DriveMate)
  const isProviderRole = action === 'provider';

  useEffect(() => {
    const id = get(parentTransaction, 'attributes.metadata.fuelChildTransactionId', null);
    if (parentTransaction && !dataLoaded && id) {
      fetchFuelChildTransaction(id);
    }
  }, [parentTransaction, dataLoaded]);

  useEffect(() => {
    if (parentTransaction && !dataLoaded) {
      setDataLoaded(true);
    }
  }, [parentTransaction, dataLoaded]);

  useEffect(() => {
    // Fetch host transaction data for providers
    if (parentTransaction?.id?.uuid && 
        isProviderRole && 
        !hostTransactionData && 
        !fetchHostTransactionInProgress) {
      dispatch(loadData({ 
        id: parentTransaction.id.uuid,
        transactionRole: 'provider'
      }));
    }
  }, [
    parentTransaction?.id?.uuid,
    isProviderRole,
    hostTransactionData,
    fetchHostTransactionInProgress,
    dispatch
  ]);


  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const isTabletLayout = viewport.width < MAX_TABLET_SCREEN_WIDTH;

  const currentListing = parentTransaction && parentTransaction.listing;
  const currentProvider = parentTransaction && ensureUser(parentTransaction.provider);
  const currentCustomer = parentTransaction && ensureUser(parentTransaction.customer);
  const listingTitle = currentListing && currentListing.attributes.title;
  const title = intl.formatMessage({ id: 'TripDetailsPage.title' }, { title: listingTitle });
  const isLoading = !dataLoaded;
  const parentCreatedAt = parentTransaction && parentTransaction.attributes.createdAt;
  const parentDateCreated = parentCreatedAt && moment(parentCreatedAt).format('Do MMM, YYYY');

  const currentUserRole =
    currentUser &&
    currentUser.id &&
    parentTransaction &&
    getUserTxRole(currentUser.id, parentTransaction);
  const isProviderRoleFromRole = currentUserRole === PROVIDER;
  const isCustomerRole = currentUserRole === CUSTOMER;
  const addonstTransactions = get(parentTransaction, 'attributes.metadata.addonsTransactions');
  const stripeReceiptUrl = get(parentTransaction, 'attributes.metadata.stripeReceiptUrl');
  const fuelPrice = fuelPriceByListing(currentListing);

  // Provider earnings breakdown logic
  const breakdownData = hostTransactionData ? {
    tripPrice: parseFloat(hostTransactionData.tripPrice),
    tripdrivelahFees: parseFloat(hostTransactionData.tripdrivelahFees),
    modifiedTripPayouts: parseFloat(hostTransactionData.modifiedTripPayouts),
    modifiedTripdrivelahFees: parseFloat(hostTransactionData.modifiedTripdrivelahFees),
    calTripPrice: parseFloat(hostTransactionData.calTripPrice),
    longTermDiscount: parseFloat(hostTransactionData.longTermDiscount || 0),
    promoDiscount: parseFloat(hostTransactionData.promoDiscount || 0),
    delivery: parseFloat(hostTransactionData.delivery || 0),
    fuelInclusionTripPrice: parseFloat(hostTransactionData.fuelInclusionTripPrice),
    fuelInclusionDrivelahFees: parseFloat(hostTransactionData.fuelInclusionDrivelahFees),
    calOtherEarnings: parseFloat(hostTransactionData.calOtherEarnings),
    mileagePackage: parseFloat(hostTransactionData.mileagePackage || 0),
    tollsTotal: parseFloat(hostTransactionData.tollsTotal),
    excessMileage: parseFloat(hostTransactionData.excessMileage),
    lateCharge: parseFloat(hostTransactionData.lateCharge),
    fuelCharge: parseFloat(hostTransactionData.fuelCharge),
    flexPlus: parseFloat(hostTransactionData.flexPlus),
    fuelRefund: parseFloat(hostTransactionData.fuelRefund),
    miscChargeToHost: parseFloat(hostTransactionData.miscChargeToHost),
    miscPayoutToHost: parseFloat(hostTransactionData.miscPayoutToHost),
    cleanlinessTotal: parseFloat(hostTransactionData.cleanlinessTotal),
    subscription: parseFloat(hostTransactionData.subscription),
    damageTotal: parseFloat(hostTransactionData.damageTotal),
    calTripEarnings: parseFloat(hostTransactionData.calTripEarnings),
  } : null;

  const renderBreakdownSection = (title, items, condition = true, showDivider = true) => {
    if (!condition) return null;

    const getHeadStyle = (isTotal, negative) => ({
      color: isTotal ? '#00A3AD' : negative ? '#D91320' : '#1A1A1A',
      fontWeight: isTotal ? 700 : 500,
    });

    const getLabelStyle = (isTotal) => ({
      color: isTotal ? '#00A3AD' : '#1A1A1A',
      fontWeight: isTotal ? 700 : 500,
    });

    const getAmount = (value, negative) => {
      if (value == null || isNaN(value)) return '';
      
      return formatMoney(
        intl,
        new Money(value, 'SGD')
      );
    };

    return (
      <div className={css.section}>
        <h3 className={css.sectionTitle}>
          <FormattedMessage id={title} />
        </h3>
        {items.map((item, index) =>
          item?.value != null && (
            <div key={index} className={css.lineItem}>
              {item.label === 'TripDetailPage.drivelahABN' ? (
                <div className={css.abnText}>
                  <FormattedMessage id={item.label} />: {item.value}
                </div>
              ) : (
                <>
                  <div className={css.lineItemContent}>
                    <span className={css.itemLabel} style={getLabelStyle(item.isTotal)}>
                      <FormattedMessage id={item.label} />
                    </span>
                    <span className={css.dottedLine}></span>
                    <span
                      className={css.itemValue}
                      style={getHeadStyle(item.isTotal, item?.negative)}
                    >
                      {getAmount(item.value, item?.negative)}
                    </span>
                  </div>
                </>
              )}
            </div>
          )
        )}
        {showDivider ? <hr className={css.sectionDivider} /> : null}
      </div>
    );
  };

  const renderBreakdown = () => {
    if (!breakdownData) {
      return (
        <div className={css.container}>
          <div className={css.section}>
            <h3 className={css.sectionTitle}>
              <FormattedMessage id="TripDetailPage.hiringEarnings" />
            </h3>
            <div className={css.lineItem}>
              <div className={css.lineItemContent}>
                <span className={css.itemLabel}>
                  <FormattedMessage id="TripDetailsPage.noEarningsData" />
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Calculate number of booking days from transaction protectedData
    const days = get(parentTransaction, 'attributes.protectedData.days', {});
    const numberOfDays = (days.regular || 0) + (days.peak || 0);
    
    // Determine discount label based on number of days
    let discountLabel = 'TripDetailPage.longTermDiscount';
    if (numberOfDays >= 30) {
      discountLabel = 'TripDetailPage.longTermDiscount'; // Keep as "Long-term discount" for 30+ days
    } else if (numberOfDays >= 7) {
      discountLabel = 'TripDetailPage.sevenDayDiscount';
    } else if (numberOfDays >= 3) {
      discountLabel = 'TripDetailPage.threeDayDiscount';
    }

    const sections = [
      {
        title: 'TripDetailPage.hiringEarnings',
        items: [
          {
            label: 'TripDetailPage.durationCharge',
            value: breakdownData.tripPrice,
          },
          ...(breakdownData.longTermDiscount !== 0 ? [{
            label: discountLabel,
            value: breakdownData.longTermDiscount,
            negative: breakdownData.longTermDiscount < 0,
          }] : []),
          ...(breakdownData.mileagePackage !== 0 ? [{
            label: 'TripDetailPage.mileagePackage',
            value: breakdownData.mileagePackage,
            negative: breakdownData.mileagePackage < 0,
          }] : []),
          ...(breakdownData.delivery !== 0 ? [{
            label: 'TripDetailPage.delivery',
            value: breakdownData.delivery,
            negative: breakdownData.delivery < 0,
          }] : []),
          ...(breakdownData.promoDiscount !== 0 ? [{
            label: 'TripDetailPage.promoDiscount',
            value: breakdownData.promoDiscount,
            negative: breakdownData.promoDiscount < 0,
          }] : []),
          {
            label: 'TripDetailPage.total',
            value: breakdownData.calTripPrice,
            isTotal: true,
            negative: breakdownData.calTripPrice < 0,
          },
        ],
      },
      {
        title: 'TripDetailPage.drivingEarnings',
        condition: breakdownData.fuelInclusionTripPrice > 0,
        items: [
          {
            label: 'TripDetailPage.fuelInclusionCharge',
            value: breakdownData.fuelInclusionTripPrice,
          },
          {
            label: 'TripDetailPage.total',
            value: breakdownData.fuelInclusionTripPrice,
            isTotal: true,
            negative: breakdownData.fuelInclusionTripPrice < 0,
          },
        ],
      },
      {
        title: 'TripDetailPage.otherEarnings',
        condition: breakdownData.calOtherEarnings > 0,
        items: [
          ...(breakdownData.tollsTotal !== 0 ? [{
            label: 'TripDetailPage.tollCharge',
            value: breakdownData.tollsTotal,
            negative: breakdownData.tollsTotal < 0,
          }] : []),
          ...(breakdownData.excessMileage !== 0 ? [{
            label: 'TripDetailPage.excessMilage',
            value: breakdownData.excessMileage,
            negative: breakdownData.excessMileage < 0,
          }] : []),
          ...(breakdownData.lateCharge !== 0 ? [{
            label: 'TripDetailPage.lateCharges',
            value: breakdownData.lateCharge,
            negative: breakdownData.lateCharge < 0,
          }] : []),
          ...(breakdownData.fuelCharge !== 0 ? [{
            label: 'TripDetailPage.fuelCharge',
            value: breakdownData.fuelCharge,
            negative: breakdownData.fuelCharge < 0,
          }] : []),
          ...(breakdownData.fuelRefund !== 0 ? [{
            label: 'TripDetailPage.fuelRefund',
            value: breakdownData.fuelRefund,
            negative: breakdownData.fuelRefund < 0,
          }] : []),
          ...(breakdownData.miscChargeToHost !== 0 ? [{
            label: 'TripDetailPage.miscCharges',
            value: breakdownData.miscChargeToHost,
            negative: breakdownData.miscChargeToHost < 0,
          }] : []),
          ...(breakdownData.miscPayoutToHost !== 0 ? [{
            label: 'TripDetailPage.miscPayout',
            value: breakdownData.miscPayoutToHost,
            negative: breakdownData.miscPayoutToHost < 0,
          }] : []),
          ...(breakdownData.cleanlinessTotal !== 0 ? [{
            label: 'TripDetailPage.cleanlinessTotal',
            value: breakdownData.cleanlinessTotal,
            negative: breakdownData.cleanlinessTotal < 0,
          }] : []),
          ...(breakdownData.damageTotal !== 0 ? [{
            label: 'TripDetailPage.totalDamage',
            value: breakdownData.damageTotal,
            negative: breakdownData.damageTotal < 0,
          }] : []),
          {
            label: 'TripDetailPage.total',
            value: breakdownData.calOtherEarnings,
            isTotal: true,
          },
        ],
      },
      {
        title: 'TripDetailPage.yourtotalEarnings',
        items: [
          {
            label: 'TripDetailPage.totalEarnings',
            value: breakdownData.calTripEarnings,
            isTotal: true,
          },
        ],
      },
    ];

    const visibleSections = sections.filter(s => s.condition === undefined ? true : !!s.condition);

    return (
      <div className={css.container}>
        {visibleSections.map((section, index) =>
          renderBreakdownSection(
            section.title,
            section.items,
            true,
            index < visibleSections.length - 1
          )
        )}
      </div>
    );
  };

  const pageProps = {
    title,
    scrollingDisabled,
    className: css.root,
  };

  const topbar = (
    <div className={css.topbar}>
      <NamedLink className={css.home} name="LandingPage">
        <Logo
          className={css.logoMobile}
          title={intl.formatMessage({
            id: 'CheckoutPage.goToLandingPage',
          })}
          format="mobile"
        />
        <Logo
          className={css.logoDesktop}
          alt={intl.formatMessage({
            id: 'CheckoutPage.goToLandingPage',
          })}
          format="desktop"
        />
      </NamedLink>
    </div>
  );

  if (isLoading) {
    return (
      <Page {...pageProps}>
        {topbar}
        <div className={css.loading}>
          <IconSpinner />
          <div>
            <FormattedMessage id={'TripDetailsPage.pleaseWait'} />
          </div>
        </div>
      </Page>
    );
  }

  const timeZone = getDefaultTimeZoneOnBrowser() || 'Asia/Singapore';
  const txBooking = parentTransaction && ensureBooking(parentTransaction.booking);
  const isFuelInclusion = get(parentTransaction, 'attributes.protectedData.isFuelInclusion') || get(parentTransaction, 'attributes.metadata.isFuelInclusion');

  const breakdown = parentTransaction && parentTransaction.id && txBooking.id && (
    <BookingBreakdownNew
      timeZone={timeZone}
      className={css.bookingBreakdown}
      userRole={currentUserRole}
      unitType={config.bookingUnitType}
      transaction={restoreTransaction(parentTransaction, isUsingMastercard, transactionRole)}
      booking={txBooking}
      shouldShowMastercardPromoLineItem={isUsingMastercard}
      isTripDetailsPage={true}
      isFuelInclusion={isFuelInclusion}
      fuelPrice={fuelPrice}
    />
  );

  return (
    <Page {...pageProps}>
      {topbar}
      <div className={css.contentContainer}>
        <div className={css.contentRow}>
          <div className={css.addonsPageTitles}>
            <div className={css.addonsBreadcrumbs}>
              {parentTransaction && (
                <NamedLink
                  className={css.arrowBack}
                  name={isCustomerRole ? 'OrderDetailsPage' : 'SaleDetailsPage'}
                  params={{ id: parentTransaction.id.uuid }}
                >
                  <span className={css.arrowBackText}>&#8249;</span>

                  {listingTitle}
                </NamedLink>
              )}
            </div>
            <div className={css.stepTitle}>
              {isProviderRole ? 'Trip earnings' : 'Trip price details'}
            </div>
          </div>
        </div>
        <div className={css.contentRow}>
          <div className={css.addonsSectionLeft}>
            <div className={css.detailsContainerMain}>
              {isProviderRole ? renderBreakdown() : (
                <>
                  <h3 className={css.containerTitle}>
                    <FormattedMessage
                      id={'TripDetailsPage.parentTransactionTitle'}
                      values={{ date: parentDateCreated }}
                    />
                  </h3>
                  {breakdown}
                </>
              )}
            </div>

            {isCustomerRole && stripeReceiptUrl ? (
              <>
                <div className={css.linkContainer}>
                  <a href={stripeReceiptUrl} className={css.linkText} target="_blank">
                    <FormattedMessage id={'TripDetailsPage.downloadInvoice'} />
                  </a>
                </div>
              </>
            ) : null}
            {(!isProviderRole && updateBookingTxs && updateBookingTxs.length > 0) ? (
              <UpdateBookingTxs
                lastUpdateBookingTx={lastUpdateBookingTx}
                updateBookingTxs={updateBookingTxs}
                timeZone={timeZone}
                currentUserRole={currentUserRole}
                isUsingMastercard={isUsingMastercard}
              />
            ) : null}
            {!isProviderRole && (
              <FuelInclusionBreakdown
                transaction={restoreTransaction(fuelTransaction, isUsingMastercard)}
                fuelTransaction={fuelTransaction}
                timeZone={timeZone}
                currentUserRole={currentUserRole}
                txBooking={txBooking}
                parentTransaction={parentTransaction}
                intl={intl}
              />
            )}
            {!isProviderRole && (
              <HostFuelPackageBlock
                isVisible={isFuelInclusion && !fuelTransaction}
              />
            )}
            {addonstTransactions &&
              addonstTransactions.map(i => {
                const childTransaction = i.childTransaction && JSON.parse(i.childTransaction);
                const txBooking = childTransaction && ensureBooking(childTransaction.booking);
                const createdAt = childTransaction && childTransaction.attributes.createdAt;
                const dateCreated = createdAt && moment(createdAt).format('Do MMM, YYYY');
                const isDelivery = i.isDelivery;
                const isExcessReduction = i.isExcessReduction;
                const isFuelInclusion = i.isFuelInclusion;

                return childTransaction ? (
                  !isDelivery && isExcessReduction && isProviderRole ? null : (
                    <div className={css.detailsContainerMain}>
                      <h3 className={css.containerTitle}>
                        <FormattedMessage
                          id={'TripDetailsPage.addonsTitle'}
                          values={{ date: dateCreated }}
                        />
                      </h3>
                      <BookingBreakdownNew
                        timeZone={timeZone}
                        className={css.bookingBreakdown}
                        userRole={currentUserRole}
                        unitType={config.bookingUnitType}
                        transaction={restoreTransaction(childTransaction, isUsingMastercard, transactionRole)}
                        booking={txBooking}
                        isAddons={true}
                        fuelPrice={fuelPrice}
                      />
                    </div>
                  )
                ) : null;
              })}
          </div>
          {isMobileLayout ? null : (
            <div className={css.addonsSectionRight}>
              <div>
                {parentTransaction && currentListing && (
                  <BookingInfoSection
                    transaction={parentTransaction}
                    isCustomer={isCustomerRole}
                    showAvatar={false}
                    listing={currentListing}
                    currentProvider={currentProvider}
                    intl={intl}
                  />
                )}
                <PartnerInfo
                  currentUserRole={transactionRole}
                  transaction={parentTransaction}
                  otherUser={isProviderRole ? currentCustomer : currentProvider}
                  show={true}
                  isMobileLayout={isMobileLayout}
                  userTypeText={intl.formatMessage({
                    id: isCustomerRole
                      ? 'TripDetailsPage.userTypeText'
                      : 'TripDetailsPage.userTypeTextHost',
                  })}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
};

TripDetailsPageComponent.defaultProps = {
  currentUser: null,
  transaction: null,
  parentTransaction: null,
};

const { bool, func, oneOf, shape, string, arrayOf, number } = PropTypes;

TripDetailsPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool.isRequired,
  transaction: propTypes.transaction,
  parentTransaction: propTypes.transaction,
  transactionRole: oneOf([PROVIDER, CUSTOMER]).isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
  fetchFuelChildTransaction: func.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {authToken} = state.Auth;
  const transaction = $bookingTx(state);
  const fuelTransactionId = get(transaction, 'attributes.metadata.fuelChildTransactionId', null);
  const fuelTransactionEntities = transaction && fuelTransactionId
    ? getMarketplaceEntities(state, [{ id: { uuid: fuelTransactionId }, type: 'transaction' }])
    : null
  ;
  const fuelTransaction = (fuelTransactionEntities && fuelTransactionEntities.length) ? fuelTransactionEntities[0] : null;

  const bookingTxId = transaction && transaction.id.uuid;
  const {
    hostTransactionData,
    fetchHostTransactionInProgress,
    fetchHostTransactionError,
    fetchTransactionInProgress,
    fetchTransactionError,
  } = state.TripDetailsPage;

  return {
    currentUser,
    transaction,
    fuelTransaction,
    accessToken:authToken ? authToken.access_token:null,
    fuelPriceByListing: $fuelPriceByListing(state),
    updateBookingTxs: $updateBookingTxsByParentTxId(state, bookingTxId),
    lastUpdateBookingTx: $lastUpdateBookingTxByParentTxId(state, bookingTxId),
    hostTransactionData,
    fetchHostTransactionInProgress,
    fetchHostTransactionError,
    fetchTransactionInProgress,
    fetchTransactionError,
  };
};

const mapDispatchToProps = dispatch => {
  return {
    dispatch,
    onFetchUpdateBookingTransaction: params => dispatch(fetchUpdateBookingTransaction(params)),
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    fetchFuelChildTransaction: id => dispatch(fetchFuelChildTransaction(id)),
  };
};

const TripDetailsPage = compose(
  withRouter,
  withViewport,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(TripDetailsPageComponent);

TripDetailsPage.loadData = loadData;

export default TripDetailsPage;
